import FormSelect from "components/form/FormSelect";
import FormText from "components/form/FormText";
import IndicatorButton from "components/indicator/IndicatorButton";
import { AppHelper } from "helpers/appHelper";
import { useApiState } from "hooks/useApiState";
import { useAppState } from "hooks/useAppState";
import { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { AcctState } from "stores/atoms/acctState";

/**
 *　カンパニーメンバー詳細画面
 */
export default function SettingMemberEdit(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type LocationStateType = {
    companyMemberId: number;
    userName: string;
    userMailAddress: string;
    roleDiv: number;
  };

  type FormStateType = {
    userName: string;
    roleDiv: number;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const location = useLocation();

  const [acctState] = useRecoilState(AcctState);

  const { updateAcctState, noticeMessageSuccess } = useAppState();
  const { postCompanyMemberUpdate, postCompanyMemberDelete } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [locationState, setLocationState] = useState<LocationStateType>(null);
  const [processingOnSave, setProcessingOnSave] = useState(false);
  const [processingOnDelete, setProcessingOnDelete] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    const locationState = location.state as LocationStateType;

    if (!locationState) {
      return;
    }

    setValue("userName", locationState.userName);
    setValue("roleDiv", locationState.roleDiv);
    setLocationState(locationState);
    setInitialized(true);
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 保存ボタン押下時の処理
   */
  const onSave = () =>
    AppHelper.submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async (data: FormStateType) => {
        try {
          // カンパニーメンバー更新APIの呼び出し
          await postCompanyMemberUpdate({
            companyMemberId: locationState.companyMemberId,
            userName: data.userName,
            roleDiv: data.roleDiv,
          });

          // 自分自身の更新の場合、アカウント関連のステートを更新
          if (_isLoginUser()) {
            await updateAcctState(["user"]);
          }

          // 完了メッセージの表示
          noticeMessageSuccess("メンバー情報の保存が完了しました。");
          // カンパニーメンバー一覧画面に遷移
          navigate(`/setting/${acctState.company.cd}/member`);
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /**
   * 削除ボタン押下時の処理
   */
  const onDelete = () =>
    AppHelper.submit(processingOnDelete, setProcessingOnDelete, async () => {
      try {
        // カンパニーメンバー削除APIの呼び出し
        await postCompanyMemberDelete({
          companyMemberId: locationState.companyMemberId,
        });

        // 完了メッセージの表示
        noticeMessageSuccess("メンバー情報の保存が完了しました。");
        // カンパニーメンバー一覧画面に遷移
        navigate(`/setting/${acctState.company.cd}/member`);
      } catch (e) {
        showBoundary(e);
      }
    });

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 本人かどうかを判定
   */
  const _isLoginUser = (): boolean => {
    return locationState.companyMemberId === acctState.company.member.id;
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {initialized && (
        <>
          <section className="section-header">
            <div className="container">
              <a
                onClick={() => navigate(-1)}
                className="btn-back-list hover mb-3 mt-1"
              >
                戻る
              </a>
              <div className="d-md-flex d-flex-header">
                <div className="flex-shrink-0 flex-left">
                  <h2 className="flex-title">
                    {locationState.userName} さんのプロフィール
                  </h2>
                </div>

                <div className="flex-grow-1 flex-right">
                  <ul className="list-inline list-btn mb-0">
                    {!_isLoginUser() && (
                      <li className="list-inline-item">
                        <button
                          onClick={onDelete}
                          className="btn btn-outline outline-blue"
                          disabled={processingOnDelete}
                        >
                          <IndicatorButton visible={processingOnDelete} />
                          ユーザーを削除する
                        </button>
                      </li>
                    )}
                    <li className="list-inline-item">
                      <button
                        onClick={onSave}
                        className="btn btn-common btn-common-w147 moderate-blue"
                        disabled={processingOnSave}
                      >
                        <IndicatorButton visible={processingOnSave} />
                        保存する
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <section className="section-profile">
            <div className="container">
              <div className="box-common">
                <form className="form-profile g-3">
                  <div className="row form-row flex-md-row justify-content-between">
                    <div className="column col-lg-6 col-xl-6 col-left">
                      <FormText
                        subject="氏名"
                        name="userName"
                        register={register}
                        errors={errors}
                        required={true}
                        maxLength={100}
                        requiredMark={true}
                      />
                    </div>
                    <div className="column col-lg-6 col-xl-6 col-right">
                      <div className="form-label">ログインメールアドレス</div>
                      <p className="ms-4 fw-bold mb-0">
                        {locationState.userMailAddress}
                      </p>
                    </div>
                  </div>
                  <div className="row form-row">
                    <div className="column col-lg-6 col-xl-6 col-left">
                      <FormSelect
                        subject="ユーザー権限"
                        name="roleDiv"
                        register={register}
                        errors={errors}
                        options={AppHelper.getDivSelectOptions("role")}
                        required={true}
                        blank={false}
                        disabled={_isLoginUser()}
                        requiredMark={true}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
